import { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Edit, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useDeleteReason,
  useGetReconnectionReasons,
} from "@features/cx/settings/hooks";

interface ReconnectionReason {
  id: number;
  code: string;
  name: string;
  longDescription: string;
  extraData: {
    responseTime: number;
    priorityLevel: number;
    resolutionTime: number;
    requiredApproval: boolean;
    acknowledgementTime: number;
    priorityLevelDisplay: string;
  };
}

interface ReconnectionReasonsTableProps {
  onEditReason: (reason: ReconnectionReason) => void;
}

export function ReconnectionReasonsTable({
  onEditReason,
}: ReconnectionReasonsTableProps) {
  const deleteReconnectionReason = useDeleteReason();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reasonToDelete, setReasonToDelete] =
    useState<ReconnectionReason | null>(null);
  const [itemsPerPage] = useState(PAGE_SIZE);
  const { data: reconnectionReasonsData } = useGetReconnectionReasons(
    currentPage,
    PAGE_SIZE
  );

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination state when data changes
  useEffect(() => {
    const totalItems = reconnectionReasonsData?.count || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    setPagination({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [reconnectionReasonsData, currentPage, itemsPerPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDeleteReason = (reason: ReconnectionReason) => {
    setReasonToDelete(reason);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (reasonToDelete) {
      const payloadDelete = {
        id: reasonToDelete.id,
      };
      deleteReconnectionReason.mutateAsync(payloadDelete);
      setShowDeleteDialog(false);
      setReasonToDelete(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "destructive";
      case "High":
        return "default";
      case "Medium":
        return "secondary";
      case "Low":
        return "outline";
      default:
        return "outline";
    }
  };

  const columns: TableColumn<ReconnectionReason>[] = [
    {
      key: "configurationCode",
      header: "Code",
      sortable: true,
      width: "w-24",
      render: (value: string) => <span className="font-mono">{value}</span>,
    },
    {
      key: "name",
      header: "Reason Name",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "longDescription",
      header: "Description",
      sortable: false,
      width: "w-64",
      render: (value: string) => (
        <span className="max-w-xs truncate block" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: "acknowledgementTime", // Changed from "extraData"
      header: "Ack. Time (Hrs)",
      sortable: true,
      width: "w-32",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.acknowledgementTime}</span>
      ),
    },
    {
      key: "responseTime", // Changed from "extraData"
      header: "Response Time (Hrs)",
      sortable: true,
      width: "w-36",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.responseTime}</span>
      ),
    },
    {
      key: "resolutionTime", // Changed from "extraData"
      header: "Resolution Time (Hrs)",
      sortable: true,
      width: "w-40",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.resolutionTime}</span>
      ),
    },
    {
      key: "priority", // Changed from "extraData"
      header: "Priority",
      sortable: true,
      width: "w-28",
      render: (value: string, row: ReconnectionReason) => (
        <Badge variant={getPriorityColor(row.extraData.priorityLevelDisplay)}>
          {row.extraData.priorityLevelDisplay}
        </Badge>
      ),
    },
    {
      key: "requiredApproval", // Changed from "extraData"
      header: "Requires Approval",
      sortable: true,
      width: "w-36",
      render: (value: boolean, row: ReconnectionReason) => (
        <Badge
          variant={row.extraData.requiredApproval ? "default" : "secondary"}
        >
          {row.extraData.requiredApproval ? "Yes" : "No"}
        </Badge>
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      onClick: (row: ReconnectionReason) => {
        onEditReason(row);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
    {
      label: "Delete",
      onClick: (row: ReconnectionReason) => {
        handleDeleteReason(row);
      },
      icon: <Trash2 className="h-4 w-4 mr-2" />,
    },
  ];

  return (
    <div className="space-y-4 mt-5">
      <GenericTable
        data={reconnectionReasonsData?.results}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No reconnection reasons found"
        loading={false}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reconnection Reason</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{reasonToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
